import { Location } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextAreaFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';

import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { VxImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { Failure } from '@libs/core/lib/state/failure';
import { withProductReviewCreate } from '@libs/mtjr/shared/generated/entities/product-review/product-review-store-features';
import { withGetProductById } from '@libs/mtjr/shared/generated/entities/product/product-store-features';
import { signalStore } from '@ngrx/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

const ProductReviewStore = signalStore(withGetProductById(), withProductReviewCreate());

@Component({
  selector: 'mac-product-feedback',
  providers: [ProductReviewStore],
  imports: [
    IonContent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonLabel,
    IonButton,
    VxLoadingComponent,
    VxErrorComponent,
    VxImageComponent,
    IonIcon,
    IonText,
    VxSubmitButtonComponent,
    VxTextAreaFieldComponent,
    IonCard,
    IonBackButton,
    IonButtons,
    VxErrorBarComponent,
    TranslatePipe,
  ],
  template: `
    @let state = store.getProductByIdState();
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-title>{{ 'FEEDBACK.PRODUCT_REVIEW' | translate }}</ion-title>

        <ion-buttons slot="start">
          <ion-back-button defaultHref="/" text="{{ 'COMMON.BACK' | translate }}" />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      @switch (state.type) {
        @case ('loading') {
          <mtjr-loading />
        }
        @case ('error') {
          <mtjr-error [failure]="state.failure" />
        }
        @case ('success') {
          @let product = state.data;
          <div class="flex justify-center">
            <div class="w-full md:mt-4 md:max-w-[550px]">
              <ion-card class="!mb-0">
                <div class="m-3 flex items-center">
                  <mtjr-img imgClass="w-32 !rounded-md" [src]="product.images?.[0]" />
                  <div class="flex flex-1 flex-col ps-4">
                    <ion-text class="font-bold">{{ product.name }}</ion-text>
                    <div class="mb-2 mt-1 flex items-center">
                      <ion-icon name="star-circle" class="me-1.5" size="small" color="success" />
                      <ion-text>{{ product.ratingAvg ?? 0 }}</ion-text>
                    </div>
                    <ion-text class="text-medium text-sm">{{ product.description }}</ion-text>
                  </div>
                </div>
              </ion-card>
              <ion-card class="!mt-3">
                <div class="flex flex-col p-4">
                  <ion-label class="text-dark ps-2 text-lg">
                    {{ 'FEEDBACK.RATE_PRODUCT' | translate }}
                  </ion-label>
                  <div class="mt-2 flex" slot="end">
                    @for (i of [1, 2, 3, 4, 5]; track $index) {
                      <ion-button fill="clear" class="me-1" shape="round" color="warning" (click)="stars.set(i)">
                        @if (stars() >= i) {
                          <ion-icon slot="icon-only" class="text-3xl" color="warning" name="star-filled" />
                        } @else {
                          <ion-icon slot="icon-only" class="text-3xl" color="warning" name="star" />
                        }
                      </ion-button>
                    }
                  </div>
                  <ion-label class="text-dark mb-3 mt-4 ps-2 text-lg">
                    {{ 'FEEDBACK.WRITE_REVIEW' | translate }}
                  </ion-label>
                  <mtjr-text-area-field
                    [field]="reviewText"
                    [rows]="5"
                    class="mx-1"
                    placeholder="Write your review here"
                  />
                  <mtjr-submit-button
                    class="my-3 h-12"
                    [state]="store.productReviewCreateState()"
                    shape="round"
                    [label]="'COMMON.SUBMIT' | translate"
                    (click)="submit()"
                  />
                </div>
              </ion-card>
              <div class="mx-5 mt-2">
                <mtjr-error-bar [failure]="store.productReviewCreateFailure() || starsFailure()" />
              </div>
            </div>
          </div>
        }
      }
    </ion-content>
  `,
})
export class AddProductReviewCreatePage {
  route = inject(ActivatedRoute);
  productId: string = this.route.snapshot.params['productId'];
  store = inject(ProductReviewStore);
  stars = signal(0);
  starsFailure = signal<Failure | null>(null);
  reviewText = inject(NonNullableFormBuilder).control('');
  router = inject(Router);
  eventBus = inject(NgEventBus);
  translate = inject(TranslateService);
  location = inject(Location);

  constructor() {
    if (!this.productId) {
      throw new Error('Product id is required');
    }
    this.store.getProductById({ id: this.productId });

    effect(() => {
      const state = this.store.productReviewCreateState();
      if (state.success) {
        untracked(() => {
          this.eventBus.cast('reviews:fetch');
        });
        this.location.back();
      }
    });

    effect(() => {
      const starsValue = this.stars();
      if (starsValue > 0) {
        untracked(() => {
          this.starsFailure.set(null);
        });
      }
    });
  }

  submit() {
    if (this.stars() === 0) {
      this.starsFailure.set(new Failure(this.translate.instant('FEEDBACK.SELECT_RATING'), 'REVIEW_RATING_REQUIRED'));
      return;
    }
    if (this.reviewText.invalid) {
      this.reviewText.markAsTouched();
      return;
    }
    this.store.productReviewCreate({
      productId: this.productId,
      rating: this.stars(),
      review: this.reviewText.value,
      orderId: this.route.snapshot.params['orderId'],
      subOrderId: this.route.snapshot.params['subOrderId'],
    });
  }
}
